/**
 * @file BulkAcceptDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Bulk Add Bills' Accept (review, 2026-10-12 — Cam's rule:
 *              "Update PO from bill is the USER'S CHOICE"). Before anything is
 *              saved, each bill being accepted is listed with every PO line
 *              whose cost it would change — PO, product, PO cost → bill cost
 *              (and the order line's cost) — each with its own tick, all
 *              ticked as Add Bill starts (none would be on a Queried bill; an
 *              accepted bill never is). The save sends exactly those choices
 *              (utils/bulkBillPlan.acceptPayload). The plans shown are worked
 *              out from the POs as they are now; a bill that no longer passes
 *              is listed as needing review and is not saved.
 */

import React, { useState } from 'react';
import Modal from './Modal';
import { BillPoCostBody } from './BillPoCostDialog';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { sourceText } from '../utils/bulkBillPlan';
import './BillPoCostDialog.css';
import './BulkAddBills.css';

function Footer({ count, busy, onConfirm, onCancel }) {
  useSlideOutButton([count, busy]);
  return (
    <div className="buttons slideout">
      <div><button className="btn-slideout" onClick={onCancel} disabled={busy}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
        <span className="btn-label">Cancel</span>
      </button></div>
      <div><button className="btn-slideout btn-slideout-primary" onClick={onConfirm} disabled={busy || !count} data-write-action
        title={count ? 'Save these bills with the PO cost choices ticked here' : 'Nothing left to save'}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
        <span className="btn-label">{`Save ${count} bill${count === 1 ? '' : 's'}`}</span>
      </button></div>
    </div>
  );
}

/**
 * @param {Array<{ item, plan }>} entries — the bills still Ready on fresh data
 * @param {Array<{ item, plan }>} dropped — the ones that now need review
 * @param {(choices: Map<number, object>) => void} onConfirm — item id → its ticks
 */
function BulkAcceptDialog({ entries, dropped = [], busy = false, onConfirm, onCancel }) {
  const [ticks, setTicks] = useState(() => new Map(entries.map(e => [e.item.id, { ...(e.plan.defaultTicks || {}) }])));
  const setTick = (itemId) => (k, on) => setTicks(prev => {
    const next = new Map(prev);
    next.set(itemId, { ...(next.get(itemId) || {}), [k]: on });
    return next;
  });
  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onCancel}
      size="xl"
      className="bulk-bills-accept"
      title={`Accept ${entries.length} bill${entries.length === 1 ? '' : 's'}?`}
      footer={<Footer count={entries.length} busy={busy} onCancel={onCancel} onConfirm={() => onConfirm(ticks)} />}
    >
      <p className="bulk-bills-accept-intro">
        Each is saved as Add Bill saves it: Approved, its PO marked billed, its PDF attached and sent to Xero.
        Supplied quantities are not copied. Where a bill's cost differs from its PO, tick the lines whose PO
        cost should become the bill's — untick any to keep the PO as it is.
      </p>
      {dropped.length > 0 && (
        <div className="bulk-bills-callout warn">
          <span className="material-symbols-rounded">rate_review</span>
          <div>
            <strong>Not saved now — {dropped.length === 1 ? 'it needs' : 'these need'} review on the POs as they are now:</strong>
            <ul>{dropped.map(d => <li key={d.item.id}>{d.item.seq}. {sourceText(d.item)} — {(d.plan.reasons || [])[0] || 'needs review'}</li>)}</ul>
          </div>
        </div>
      )}
      <div className="bulk-bills-accept-list">
        {entries.map(({ item, plan }) => (
          <section key={item.id} className="bulk-bills-accept-bill">
            <h4>
              {item.seq}. {plan.supplier?.name || 'Supplier'} · {plan.supplierReference || '—'} · {formatPrice(plan.totals?.total)}
              <span className="muted"> — {sourceText(item)}</span>
            </h4>
            {plan.costChanges?.length ? (
              <BillPoCostBody
                changes={plan.costChanges}
                ticks={ticks.get(item.id) || {}}
                setTick={setTick(item.id)}
                queried={false}
                busy={busy}
                idPrefix={`bulk-accept-${item.id}`}
                intro={`${plan.poNumbers?.join(', ') || 'Its PO'}: tick the lines whose cost should become this bill's.`}
              />
            ) : (
              <p className="muted">No PO cost differs from this bill.</p>
            )}
          </section>
        ))}
      </div>
    </Modal>
  );
}

export default BulkAcceptDialog;
